import type { ReactElement } from "react";
import {
  containmentRollup,
  type ContainmentPlanParams,
} from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { ContainmentActionSummary } from "./ContainmentActionSummary.js";
import { sortByAttention } from "./presentation.js";

export const CONTAINMENT_VISIBLE_LIMIT = 3;

export function ContainmentSummary({
  data,
}: {
  data: ContainmentPlanParams;
}): ReactElement {
  const rollup = containmentRollup(data.actions);
  const ineffective = rollup.byEffectiveness["Not effective"];
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-pb-text-muted">
      <span>
        {rollup.total} action{rollup.total === 1 ? "" : "s"}
      </span>
      <span className={rollup.overdue > 0 ? "text-pb-red-text" : undefined}>
        {rollup.overdue} overdue
      </span>
      <span className={rollup.unowned > 0 ? "text-pb-amber-text" : undefined}>
        {rollup.unowned} unowned
      </span>
      <span className={ineffective > 0 ? "text-pb-red-text" : undefined}>
        {ineffective} not effective
      </span>
    </p>
  );
}

export function ContainmentPlanWidget({
  data,
}: WidgetBodyProps<ContainmentPlanParams>): ReactElement {
  if (data.actions.length === 0) {
    return (
      <div className="grid flex-1 place-items-center px-3 py-3 text-center text-xs italic text-pb-text-muted">
        No containment actions yet — expand to add one for each escape point.
      </div>
    );
  }
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <ContainmentSummary data={data} />
      <ul
        aria-label="Containment actions"
        className="min-h-0 flex-1 divide-y divide-pb-border overflow-auto"
      >
        {sortByAttention(data.actions)
          .slice(0, CONTAINMENT_VISIBLE_LIMIT)
          .map((action) => (
            <li key={action.id} className="py-3">
              <ContainmentActionSummary action={action} />
            </li>
          ))}
      </ul>
    </div>
  );
}
